import { useQuery } from "@tanstack/react-query";
import { LogOut, Menu, Moon, Sun, Languages, UserRound } from "lucide-react";
import { systemService } from "@/api/services";
import { useI18n } from "@/i18n/I18nProvider";
import { useTheme } from "@/theme/ThemeProvider";
import { SearchInput } from "@/components/common/SearchInput";
import { cn } from "@/lib/cn";
import { useState } from "react";
import { useAuth } from "@/auth/AuthProvider";
import { CURRENT_APP_ENVIRONMENT, isPreviewEnvironment } from "@/config/preview";

interface HeaderProps {
  onOpenSidebar: () => void;
}

export const Header = ({ onOpenSidebar }: HeaderProps) => {
  const { t, lang, toggleLang } = useI18n();
  const { theme, toggleTheme } = useTheme();
  const [query, setQuery] = useState("");
  const { identity, logout } = useAuth();
  const preview = isPreviewEnvironment(CURRENT_APP_ENVIRONMENT);

  const health = useQuery({
    queryKey: ["health"],
    queryFn: systemService.health,
    retry: false,
    staleTime: 30_000,
  });

  const statusLabel = health.isLoading
    ? t.header.backendChecking
    : health.data?.status === "ok"
      ? t.header.backendOnline
      : t.header.backendOffline;

  return (
    <header
      data-testid="app-header"
      className="sticky top-0 z-20 flex h-14 items-center gap-3 border-b border-border bg-card px-4 lg:px-6"
    >
      <button
        type="button"
        data-testid="header-menu-button"
        aria-label={t.header.menu}
        onClick={onOpenSidebar}
        className="ops-focus rounded-sm p-1.5 text-muted-foreground transition-colors duration-150 hover:text-foreground lg:hidden"
      >
        <Menu className="h-5 w-5" />
      </button>

      {preview ? (
        <span
          data-testid="preview-environment-badge"
          className="rounded-sm border border-amber-500/40 bg-amber-500/10 px-2.5 py-1 text-xs font-bold tracking-wide text-amber-700 dark:text-amber-300"
        >
          {lang === "fr" ? "PREVIEW · LECTURE SEULE" : "PREVIEW · READ ONLY"}
        </span>
      ) : (
        <SearchInput
          value={query}
          onChange={setQuery}
          placeholder={t.header.search}
          className="max-w-md flex-1"
          testId="header-search"
        />
      )}

      <div className="ml-auto flex items-center gap-2">
        <span
          data-testid="backend-status"
          title={statusLabel}
          className="hidden items-center gap-2 rounded-sm border border-border px-2.5 py-1 text-xs font-medium text-muted-foreground sm:flex"
        >
          <span
            className={cn(
              "h-2 w-2 rounded-full",
              health.isLoading
                ? "bg-slate-400"
                : health.data?.status === "ok"
                  ? "bg-emerald-500"
                  : "bg-red-500",
            )}
          />
          {statusLabel}
        </span>

        <button
          type="button"
          data-testid="lang-toggle-button"
          onClick={toggleLang}
          aria-label={t.header.lang}
          className="ops-focus flex items-center gap-1.5 rounded-sm border border-border px-2 py-1.5 text-xs font-semibold uppercase tracking-wider text-muted-foreground transition-colors duration-150 hover:text-foreground"
        >
          <Languages className="h-4 w-4" />
          {lang}
        </button>

        <button
          type="button"
          data-testid="theme-toggle-button"
          onClick={toggleTheme}
          aria-label={t.header.theme}
          className="ops-focus rounded-sm border border-border p-1.5 text-muted-foreground transition-colors duration-150 hover:text-foreground"
        >
          {theme === "light" ? <Moon className="h-4 w-4" /> : <Sun className="h-4 w-4" />}
        </button>

        <span
          data-testid="header-user"
          className="flex items-center gap-2 border-l border-border pl-3 text-sm text-muted-foreground"
        >
          <UserRound className="h-4 w-4" />
          <span className="hidden sm:inline">{identity?.display_name ?? identity?.object_id ?? t.header.operator}</span>
        </span>
        <button
          type="button"
          onClick={() => void logout()}
          aria-label="Se déconnecter"
          title="Se déconnecter"
          className="ops-focus rounded-sm border border-border p-1.5 text-muted-foreground transition-colors duration-150 hover:text-foreground"
        >
          <LogOut className="h-4 w-4" />
        </button>
      </div>
    </header>
  );
};
